import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import type {HoldingStatusResponse} from '@/types/holding-status'

/* eslint-disable i18next/no-literal-string -- query-key roots */
const ACCESS_PROFILE_HOLDINGS_KEY = 'access-profile-holdings'
const FUNCTION_GROUP_HOLDINGS_KEY = 'function-group-holdings'
/* eslint-enable i18next/no-literal-string */

/**
 * Picker-decoration data hook (issue 1043). Loads the provenance-aware holding
 * status for each {@code accessProfileId} the picker currently shows for the
 * given beneficiary. Disabled when {@code beneficiaryIdentityId} is undefined
 * or {@code accessProfileIds} is empty — keeps the network silent until both
 * inputs are real.
 *
 * <p>The returned map is keyed by {@code accessProfileId}; ids not present in
 * the map should be treated as {@link HoldingStatusKind#NONE} (e.g. while the
 * query is loading, or when the backend's response races a picker remount).
 */
export function useAccessProfileHoldings(
    beneficiaryIdentityId: string | undefined,
    accessProfileIds: ReadonlyArray<string>,
) {
    const services = useServices()
    const idsKey = [...accessProfileIds].sort().join(',')
    return useQuery<Record<string, HoldingStatusResponse>>({
        queryKey: [ACCESS_PROFILE_HOLDINGS_KEY, beneficiaryIdentityId, idsKey],
        queryFn: () => services.accessRequests.profileHoldings(
            beneficiaryIdentityId!, [...accessProfileIds]),
        enabled: !!beneficiaryIdentityId && accessProfileIds.length > 0,
    })
}

/**
 * FG-side mirror of {@link useAccessProfileHoldings}. Same semantics — disabled
 * when inputs aren't ready; map keyed by {@code functionGroupId}.
 */
export function useFunctionGroupHoldings(
    beneficiaryIdentityId: string | undefined,
    functionGroupIds: ReadonlyArray<string>,
) {
    const services = useServices()
    const idsKey = [...functionGroupIds].sort().join(',')
    return useQuery<Record<string, HoldingStatusResponse>>({
        queryKey: [FUNCTION_GROUP_HOLDINGS_KEY, beneficiaryIdentityId, idsKey],
        queryFn: () => services.functionGroupRequests.holdings(
            beneficiaryIdentityId!, [...functionGroupIds]),
        enabled: !!beneficiaryIdentityId && functionGroupIds.length > 0,
    })
}
